import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    fetchAppPermissionGroups,
    type AdminRegistryApp,
    type AppPermissionGroup,
} from '@/api/console';
import { Button, Callout, EmptyState, ErrorState, Input, Modal } from '@/components/ui';
import styles from './AppPermissionGroupsModal.module.css';

export interface AppPermissionGroupsModalProps {
    /** The app whose permission-group catalog is being viewed; `null` closes the modal. */
    app: AdminRegistryApp | null;
    onClose: () => void;
}

/** Same magnifier glyph `AppsToolbar`'s search box already uses. */
function SearchIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
    );
}

/** Client-side filter over the already-fetched group list — no search endpoint exists. */
function matchesQuery(group: AppPermissionGroup, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    if (group.name.toLowerCase().includes(q)) return true;
    if (group.key.toLowerCase().includes(q)) return true;
    return group.permissions.some((code) => code.toLowerCase().includes(q));
}

/**
 * EAA-1846: row-menu "Xem nhóm quyền" modal (design frame 44) — a read-only
 * view of an app's `TenantPermissionGroup` catalog (system groups + the
 * acting org's own custom groups), each with its permission-code list.
 *
 * Deliberately NOT an editor. `GET /admin/apps/{code}/permission-groups` is a
 * read endpoint; there is no create/update/delete API for a group behind this
 * screen, so "Sửa" (for org-owned groups) and the footer's "Tạo nhóm quyền"
 * both render per the design's visual language but are inert — see the
 * `title` on each explaining why, rather than wiring a fake success path.
 *
 * `available: false` is a normal 200, not a fetch error (e.g. the app has no
 * ACH-side tenant scope for the acting org) — rendered as an `EmptyState`
 * with the server's own `reason` string, distinct from `groupsQuery.isError`
 * (a real network/5xx failure), which gets `ErrorState` + retry.
 *
 * The "Nhóm hệ thống không sửa và không xoá được" callout renders
 * unconditionally whenever the catalog is available, matching the design
 * export literally: that export's own example has an ORG group selected
 * (Giáo viên EMG) yet still shows the callout, so it reads as a standing
 * footnote about the `sys-*` convention rather than something toggled by
 * which row is expanded.
 */
export function AppPermissionGroupsModal({ app, onClose }: AppPermissionGroupsModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    const [query, setQuery] = useState('');
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    const groupsQuery = useQuery({
        queryKey: ['app-permission-groups', app?.code, accessToken],
        queryFn: () =>
            fetchAppPermissionGroups(config.apiBase, accessToken as string, app?.code as string),
        enabled: app !== null,
        retry: false,
    });

    const groups = useMemo(() => groupsQuery.data?.groups ?? [], [groupsQuery.data]);
    const visibleGroups = useMemo(
        () => groups.filter((group) => matchesQuery(group, query)),
        [groups, query]
    );
    const selectedGroup = groups.find((group) => group.key === selectedKey) ?? null;

    function handleClose() {
        setQuery('');
        setSelectedKey(null);
        onClose();
    }

    if (!app) return null;

    const available = groupsQuery.data?.available ?? false;

    return (
        <Modal
            open={app !== null}
            onCancel={handleClose}
            title={t('console.apps.permissionGroupsModal.title', { name: app.name })}
            width={560}
            footer={
                <div className={styles.footer}>
                    <span className={styles.footerHint}>
                        {t('console.apps.permissionGroupsModal.footerHint')}
                    </span>
                    <Button onClick={handleClose}>{t('common.close')}</Button>
                    <Button
                        type="primary"
                        disabled
                        title={t('console.apps.permissionGroupsModal.createGroupDisabledTitle')}
                    >
                        {t('console.apps.permissionGroupsModal.createGroupButton')}
                    </Button>
                </div>
            }
        >
            <p className={styles.intro}>
                <Trans
                    i18nKey="console.apps.permissionGroupsModal.intro"
                    components={{ bold: <strong />, code: <code className={styles.introCode} /> }}
                />
            </p>

            {groupsQuery.isLoading && (
                <p aria-busy="true">{t('console.apps.permissionGroupsModal.loading')}</p>
            )}

            {groupsQuery.isError && (
                <ErrorState
                    description={t('console.apps.permissionGroupsModal.errorDescription')}
                    onRetry={() => groupsQuery.refetch()}
                    retrying={groupsQuery.isFetching}
                />
            )}

            {groupsQuery.data && !available && (
                <EmptyState
                    title={t('console.apps.permissionGroupsModal.unavailableTitle')}
                    description={groupsQuery.data.reason}
                />
            )}

            {groupsQuery.data && available && (
                <>
                    <Input
                        className={styles.search}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t('console.apps.permissionGroupsModal.searchPlaceholder')}
                        aria-label={t('console.apps.permissionGroupsModal.searchAriaLabel')}
                        prefix={<SearchIcon />}
                    />

                    {visibleGroups.length === 0 ? (
                        <EmptyState
                            title={t('console.apps.permissionGroupsModal.noResultsTitle')}
                        />
                    ) : (
                        <ul className={styles.list}>
                            {visibleGroups.map((group) => (
                                <li key={group.key}>
                                    <button
                                        type="button"
                                        className={styles.row}
                                        aria-expanded={selectedKey === group.key}
                                        onClick={() =>
                                            setSelectedKey((current) =>
                                                current === group.key ? null : group.key
                                            )
                                        }
                                    >
                                        <span
                                            className={`${styles.pill} ${
                                                group.is_system
                                                    ? styles.pillSystem
                                                    : styles.pillOrg
                                            }`}
                                        >
                                            {group.is_system
                                                ? t('console.apps.permissionGroupsModal.systemPill')
                                                : t('console.apps.permissionGroupsModal.orgPill')}
                                        </span>
                                        <span className={styles.nameCol}>
                                            <span className={styles.name}>
                                                {group.name}
                                            </span>
                                            <code className={styles.key}>
                                                {group.key}
                                            </code>
                                        </span>
                                        <span className={styles.count}>
                                            {t(
                                                'console.apps.permissionGroupsModal.permissionCount',
                                                { count: group.permission_count }
                                            )}
                                        </span>
                                        <span className={styles.action}>
                                            {group.is_system ? (
                                                t('console.apps.permissionGroupsModal.viewOnlyAction')
                                            ) : (
                                                <span
                                                    className={styles.actionEdit}
                                                    title={t(
                                                        'console.apps.permissionGroupsModal.editDisabledTitle'
                                                    )}
                                                >
                                                    {t('console.apps.permissionGroupsModal.editAction')}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {selectedGroup && (
                        <div className={styles.detail}>
                            <span className={styles.detailLabel}>
                                {t('console.apps.permissionGroupsModal.detailLabel', {
                                    name: selectedGroup.name,
                                })}
                            </span>
                            <div className={styles.chips}>
                                {selectedGroup.permissions.map((code) => (
                                    <code key={code} className={styles.chip}>
                                        {code}
                                    </code>
                                ))}
                            </div>
                            <p className={styles.detailNote}>
                                <Trans
                                    i18nKey="console.apps.permissionGroupsModal.tenantSelfReadNote"
                                    components={{
                                        code1: <code className={styles.detailNoteCode} />,
                                        code2: <code className={styles.detailNoteCode} />,
                                    }}
                                />
                            </p>
                        </div>
                    )}

                    <Callout
                        tone="warning"
                        label={t('console.apps.permissionGroupsModal.systemWarningLabel')}
                        longLabel
                    >
                        <Trans
                            i18nKey="console.apps.permissionGroupsModal.systemWarningBody"
                            components={{
                                        code1: <code className={styles.detailNoteCode} />,
                                        code2: <code className={styles.detailNoteCode} />,
                                    }}
                        />
                    </Callout>
                </>
            )}
        </Modal>
    );
}

export default AppPermissionGroupsModal;
